/**
 * beforeLoad guard for /company/* admin routes (Milestone 7).
 *
 * Server is still the source-of-truth (the gateway re-checks
 * memberships on every request), so this is purely a UX hardening:
 * non-admins get bounced to /boards instead of seeing a half-empty
 * Company page.
 *
 * Strategy:
 *   - The active org id is in localStorage (set by ActiveOrgProvider).
 *   - We pre-fetch /api/orgs/:id/members/me via the queryClient so
 *     the data is already there when the page mounts.
 *   - If membership exists and org_role ∈ {founder, admin} → allow.
 *   - Otherwise → throw redirect to /boards.
 *
 * If we don't have an active org id yet (very first paint of a brand
 * new login) we let the route through and rely on the in-page hooks to
 * gate writes. The shell.tsx ActivityBar hides the Company icon when
 * !canAdminOrg so this is rarely hit.
 */
import { redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { orgsApi, orgsKeys } from './api/orgs';

const STORAGE_KEY = 'chaos.active_org_id';

function readActiveOrgId(): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Use inside `createFileRoute(...)({ beforeLoad: ... })`. Throws a
 * `redirect()` if the caller is not org founder/admin. The queryClient
 * is taken from the route context (TanStack Router exposes it via the
 * root route in Shell when configured); we fall back to a fetch if it
 * isn't available so the guard never silently misfires.
 */
export async function requireOrgAdminBeforeLoad(opts: {
  context?: { queryClient?: QueryClient };
}): Promise<void> {
  const orgId = readActiveOrgId();
  // First login or org list not yet hydrated → let through; page-level
  // hooks (useCanAdminOrg) still gate writes.
  if (!orgId) return;

  let me: Awaited<ReturnType<typeof orgsApi.getMyMembership>>;
  try {
    if (opts.context?.queryClient) {
      me = await opts.context.queryClient.fetchQuery({
        queryKey: orgsKeys.myMembership(orgId),
        queryFn: () => orgsApi.getMyMembership(orgId),
        staleTime: 30_000,
      });
    } else {
      me = await orgsApi.getMyMembership(orgId);
    }
  } catch {
    // Network glitch or 401 — let the in-page hooks deal with it.
    return;
  }

  const role = me.membership?.orgRole;
  if (role === 'founder' || role === 'admin') return;
  throw redirect({ to: '/boards' });
}
